import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { MIN_PASSWORD_LENGTH, passwordSchema } from '@nest-recruit/shared';
import { z } from 'zod';
import { Button } from '../../../components/Button';
import { FloatingField } from '../../../components/FloatingField';
import { useResetPassword } from '../api/auth-api';
import { AuthError, AuthLayout, AuthNotice } from '../components/AuthLayout';
import { applyFieldErrors, hasFieldErrors } from './LoginPage';
import styles from './auth-forms.module.css';

/**
 * AUTH-03 — choose a new password from a reset link.
 *
 * The confirm field lives here rather than in the shared schema because it is a
 * property of this form, not of the API: the server receives one password and has
 * no opinion about how many boxes it was typed into.
 */
const resetFormSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Both passwords must match.',
    path: ['confirmPassword'],
  });

type ResetFormValues = z.infer<typeof resetFormSchema>;

export function ResetPasswordPage(): ReactNode {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const reset = useResetPassword();

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetFormSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    form.setFocus('password');
  }, [form]);

  const onSubmit = form.handleSubmit(async (values) => {
    await reset
      .mutateAsync({ token: search.token ?? '', password: values.password })
      .catch((error: unknown) => {
        applyFieldErrors(error, form.setError);
      });
  });

  if (!search.token) {
    return (
      <AuthLayout
        title="This link is incomplete"
        tone="danger"
        subtitle="It is missing the code that makes it work."
        footer={
          <Link to="/forgot-password" className={styles.link}>
            Ask for a new link
          </Link>
        }
      >
        <p className={styles.meta}>
          Open the link from the email itself, or ask for a new one — they are quick to send.
        </p>
      </AuthLayout>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthLayout title="Password changed" icon={<CheckCircle2 size={20} aria-hidden />}>
        <AuthNotice tone="success">
          You can sign in with your new password. Any other device that was signed in has been
          signed out.
        </AuthNotice>
        <Button variant="solid" size="lg" block onClick={() => void navigate({ to: '/login' })}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle={`At least ${MIN_PASSWORD_LENGTH} characters, and not one you have used here before.`}
      footer={
        <Link to="/login" className={styles.link}>
          Back to sign in
        </Link>
      }
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        {reset.error && !hasFieldErrors(reset.error) && <AuthError message={reset.error.message} />}

        <FloatingField
          {...form.register('password')}
          label="New password"
          error={form.formState.errors.password?.message}
          isRequired
          type="password"
          autoComplete="new-password"
        />

        <FloatingField
          {...form.register('confirmPassword')}
          label="Type it again"
          error={form.formState.errors.confirmPassword?.message}
          isRequired
          type="password"
          autoComplete="new-password"
        />

        <div className={styles.actions}>
          <Button type="submit" variant="solid" size="lg" block isPending={reset.isPending}>
            Save the password
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
